import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SecurityContext, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DrawerModule } from 'primeng/drawer';
import { RiskOfficerConfirmPopup } from '../risk-officer-confirm-popup/risk-officer-confirm-popup';
import { Api } from '../../services/api';
import { Toaster } from '../../services/toaster';
import { Auth } from '../../services/auth';
import { SupervisorUpdateLogs } from '../supervisor-update-logs/supervisor-update-logs';
import { LoaderService } from '../../services/loader';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { fadeInAnimation } from '../../animation';
import { WidthHeightDirective } from '../../widthHeightDirective';
import { NohtmlDirective } from '../../directives/nohtml.directive';
import { riskParameters } from './riskParameters';
import { Common } from '../../services/common';
import { DomSanitizer } from '@angular/platform-browser';

interface RiskItemPayload {
  IssueId: number;
  RiskStatement: string;
  RiskDescription: string;
  ImpactId: number;
  RiskSeverity: number;
  RiskStatus: number;
  FTE: number;
  BusinessImpact: string;
  RevenueImpact: number;
  MarginImpact: number;
  CurrentState: string;
  OverallSummary: string;
  IsRisk: boolean;
  Comments: string;
  ReasonId: number;
}

interface SavePayload {
  RiskLst: RiskItemPayload[];
  AssociateId: string;
  PFLMId: number;
  IsNewRiskList: boolean;
}

@Component({
  selector: 'app-notes',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NohtmlDirective,
    DrawerModule, RiskOfficerConfirmPopup, TooltipModule, WidthHeightDirective],
  templateUrl: './notes.html',
  styleUrl: './notes.scss',
  providers: [BsModalService, DecimalPipe],
  animations: [fadeInAnimation]
})
export class Notes implements OnInit, OnDestroy {
  @Output() noteBlockEvent = new EventEmitter<boolean>();
  @Input() notesData!: any;

  // Default flags
  isSummaryEdit: boolean = false;
  showSummaryContent: boolean = true;

  // Sample data
  riskSummary: string = '';
  inputRequiredError: string = 'Summary input is required.';

  // Icons (use placeholder paths for now)
  editIcon = 'assets/images/summary-edit-icon.svg';
  saveIcon = 'assets/images/save-notes-icon.svg';
  saveIconDisabled = 'assets/images/save-notes-disabled.svg';
  summaryMinimizeIcon = 'assets/images/summary-minimize.svg';
  riskIcon = 'assets/images/risk-icon.svg';
  showData = true;
  updatedBtn = 'assets/images/updated-btn.png';

  // Flags
  isRisksEdit: boolean = false;
  isSidebarVisible = false;
  selectedPflmId = 0; // sample numeric ID 
  selectedFrequency = 'Daily';
  selectedAssociateId = '';
  private subscription: Subscription | null = null;
  private issuesFromApi: any[] = [];

  risks: any[] = [];
  // readOnlyRisk = [] as any;
  readOnlyRisk: number[] = [];

  severityData = [] as any;
  statusData = [] as any;
  impactAreaData = [] as any;
  role: string = '';
  reasonsData: any[] = [];

  // Form setup
  risksForm: FormGroup;
  originalRiskComments: string[] = [];

  userDetails: any = {};
  modalRef?: BsModalRef;
  riskHighClass = 'risk-severity-high';
  riskMediumClass = 'risk-severity-medium';
  riskLowClass = 'risk-severity-low';
  updatedConversationIds: string[] = [];
  riskParameters: any;
  private sanitizer = inject(DomSanitizer);

  constructor(private fb: FormBuilder, public apiService: Api,
    private toasterService: Toaster, private authService: Auth,
    private modalService: BsModalService,
    private loaderService: LoaderService,
    private decimalPipe: DecimalPipe,
    private readonly commonService: Common) {
    this.riskParameters = riskParameters;

    this.risksForm = this.fb.group({
      risks: this.fb.array([])
    });
  }

  ngOnInit() {
    // this.readOnlyRisk = this.risks;
    this.userDetails = this.authService.getUserDetails();
    this.role = this.userDetails.role || '';
    this.fetchDropdownData();
    this.subscription = this.apiService.notesState$.subscribe(state => {
      if (state.open && state.data) {
        this.isSummaryEdit = false;
        this.isRisksEdit = false;
        const payload = state.data?.data;   // ← the actual API payload

        // Clear previous conversation IDs
        this.updatedConversationIds = [];

        if (!payload) return;
        const header = payload.header || {};
        this.selectedAssociateId = String(header.associate_id ?? '');
        this.selectedPflmId = Number(header.pflm_id ?? 0);

        // 1) Summary
        this.riskSummary = this.sanitizeHtml(payload.header?.overall_summary || '');

        // 2) Risks (simple mapping)
        this.risks = (payload.issues || []).map((it: any) => {
          if (it.update_conversation_id) {
            this.updatedConversationIds.push(it.update_conversation_id);
          }
          return {
            IssueId: it.issue_id,
            IsRisk: it.is_risk,
            RiskStatment: this.sanitizeHtml(it.risk_statement || ''),
            RiskDescription: this.sanitizeHtml(it.risk_description || '').substring(0, riskParameters.maxRiskDescription) || '',
            RiskSeverity: it.risk_severity,
            IssueStatusId: it.issue_status_id,
            ImpactId: it.impact_id,
            BusinessImpact: this.sanitizeHtml(it.business_impact || '').substring(0, riskParameters.maxBusinessImpact) || '',
            RevenueImpact: it.revenue_impact,
            MarginImpact: it.margin_impact,
            FTE: it.fte,
            Comments: this.sanitizeHtml(it.comments || '').substring(0, riskParameters.maxComments) || '',
            ReasonId: it.reason_id,
            current_state: it.current_state,
            is_updated: Boolean(it.is_updated),
            update_conversation_id: it.update_conversation_id
          };
        });

        // Save raw issues for edit mode
        this.issuesFromApi = payload?.issues || [];

        this.readOnlyRisk = this.risks
          .filter(r => r.IsRisk === true)
          .map(r => Number(r.IssueId));

        this.originalRiskComments = this.risks.map(r => r?.Comments ?? '');

        // 4) Build FormArray for view mode as well, so handlers work
        this.rebuildRisksFormArray(this.issuesFromApi);
      }
    });
  }

  ngOnDestroy(): void {
    this.originalRiskComments = [];
    this.updatedConversationIds = [];
    this.subscription?.unsubscribe();
  }


  private getIssueIdsCSV(): string {
    if (!this.risks || !this.risks.length) return '';
    return this.risks.map(r => r.IssueId).filter(id => id).join(',');
  }


  openUpdateLogs(risk: any) {
    this.apiService.supervisorUpdateLogContext$.next({
      issueId: risk.IssueId,
      pflmId: this.selectedPflmId
    });

    this.modalRef = this.modalService.show(SupervisorUpdateLogs, {
      class: 'modal-md'   // optional size
    });
  }

  getChatHistory() {
    const issueIdsCSV = this.getIssueIdsCSV();
    this.apiService.showChatHistory$.next(true);
    this.apiService.chatHistoryIssueIds$.next(issueIdsCSV);
    this.apiService.showAuditLog$.next(false);
    this.apiService.updatedConversationIds$.next(this.updatedConversationIds);
  }

  getAuditLog() {
    const issueIdsCSV = this.getIssueIdsCSV();
    this.apiService.showAuditLog$.next(true);
    this.apiService.auditLogIssueIds$.next(issueIdsCSV);
    this.apiService.auditLogPflmId$.next(this.selectedPflmId);
    this.apiService.showChatHistory$.next(false);
  }

  closeNotes() {
    this.noteBlockEvent.emit(false);
  }

  // Methods to handle UI actions
  onEditSummary(): void {
    this.isSummaryEdit = true;
  }

  onSaveSummary(): void {
    this.toasterService.clearAll();
    this.loaderService.show();

    const summary = this.riskSummary?.trim() ?? '';
    if (!summary) return;

    if (this.isROFormInvalid()) {
      this.loaderService.hide();
      return;
    }

    const issues = Array.isArray(this.issuesFromApi) ? this.issuesFromApi : [];
    if (!issues.length) {
      console.warn('[Notes] Save summary: no issues available to build RiskLst.');
      return;
    }

    // Helper: statusId -> human-readable text (for CurrentState)
    const resolveStatusText = (statusId: number): string => {
      if (!statusId || !Array.isArray(this.statusData)) return 'Unknown';
      const s = this.statusData.find((it: any) =>
        it.issue_status_id === statusId || it.IssueStatusId === statusId
      );
      return s ? (s.issue_description || s.IssueStatus || 'Unknown') : 'Unknown';
    };

    // Map raw issues to the payload shape
    const RiskLst = issues.map((it: any) => ({
      IssueId: Number(it.issue_id ?? 0),
      RiskStatement: String(it.risk_statement ?? '').trim(),
      RiskDescription: String(it.risk_description ?? '').trim(),
      ImpactId: Number(it.impact_id ?? 0),
      RiskSeverity: Number(it.risk_severity ?? 0),
      RiskStatus: Number(it.issue_status_id ?? 0),
      FTE: Number(it.fte ?? 0),
      BusinessImpact: String(it.business_impact ?? '').trim(),
      RevenueImpact: it.revenue_impact != null ? Number(it.revenue_impact) : 0,
      MarginImpact: it.margin_impact != null ? Number(it.margin_impact) : 0,
      CurrentState: String(it.current_state ?? '').trim(),
      OverallSummary: summary,
      IsRisk: Boolean(it.is_risk),
      Comments: it.comments != null ? String(it.comments) : null,
      ReasonId: it.reason_id ?? null
    }));

    const payload = {
      RiskLst,
      AssociateId: String(this.selectedAssociateId ?? ''),
      PFLMId: Number(this.selectedPflmId ?? 0),
      IsNewRiskList: false,
    };

    this.apiService.saveRisksAPI(payload).subscribe({
      next: (res) => {
        this.toasterService.setError({ message: 'Summary successfully updated.', type: 'success' });
        this.loaderService.hide();
        this.isSummaryEdit = false;
        this.getNotes();
        this.apiService.triggerRefresh();
      },
      error: (err) => {
        this.toasterService.setError({ message: 'Unable to save the data.', type: 'error' });
        this.loaderService.hide();
        console.error('Summary save failed →', err);
      }
    });
  }

  private fetchDropdownData(): void {
    this.apiService.GetMasterDataForDropdown().subscribe({
      next: (res: any) => {
        this.severityData = res?.severities || [];
        this.impactAreaData = res?.impact_areas || [];

        // ✅ Only "Open" (2) and "WIP" (3)
        const allowedStatusIds = new Set([2, 3]);
        this.statusData = (res?.statuses ?? []).filter(
          (st: any) => allowedStatusIds.has(st?.issue_status_id)
        );


        // ✅ NEW: load reasons from API
        const reasons = res?.reasons ?? res?.Reasons ?? [];
        this.reasonsData = reasons.map((r: any) => ({
          id: r?.id ?? null,
          reason: r?.reason ?? ''
        }));
      },
      error: (err: any) => {
        console.error("Dropdown API error:", err);
        this.toasterService.setError({
          message: 'Unable to fetch the data.',
          type: 'error',
        });
      }
    });
  }

  private buildRiskFormGroup(issue: any): FormGroup {
    // Expecting raw API shape here (issue_id, impact_id, issue_status_id, severity_id, etc.)
    const DEFAULT_SEVERITY_HIGH = 3;
    const isRO = this.isRiskOfficer;
    return this.fb.group({
      // Display-only or used in labels (not required, but handy)
      issue_id: [issue.issue_id],

      // Editable fields (match your template formControlName)
      RiskStatment: [this.sanitizeHtml(issue.risk_statement || ''), [Validators.maxLength(riskParameters.maxRiskStatement)]],
      RiskDescription: [this.sanitizeHtml(issue.risk_description || '').substring(0, riskParameters.maxRiskDescription) || '', [Validators.required, Validators.maxLength(riskParameters.maxRiskDescription)]],

      // Must match dropdown option IDs:
      Risk_Severity: [issue.severity_id || DEFAULT_SEVERITY_HIGH, [Validators.required]],

      Status: [issue.issue_status_id, [Validators.required]],

      Impact_Area: [issue.impact_id, [Validators.required]],

      Business_Impact: [this.sanitizeHtml(issue.business_impact || '').substring(0, riskParameters.maxBusinessImpact) || '', [Validators.required, Validators.maxLength(riskParameters.maxBusinessImpact)]],
      Revenue_Impact: [issue.revenue_impact ?? null, isRO ? [Validators.required, Validators.min(0)] : [Validators.min(0)]],
      Margin_Impact: [issue.margin_impact ?? null, isRO ? [Validators.required, Validators.min(0)] : [Validators.min(0)]],
      Total_FTE: [
        Number(issue.fte ?? 0),
        isRO
          ? [Validators.required, Validators.min(0), Validators.max(99999), Validators.pattern('^[0-9]{0,5}$')]
          : [Validators.min(0), Validators.max(99999), Validators.pattern('^[0-9]{0,5}$')]
      ],

      Comments: [this.sanitizeHtml(issue.comments || '').substring(0, riskParameters.maxComments) || '', [Validators.required, Validators.maxLength(riskParameters.maxComments)]],

      // Reason is hardcoded options now; keep as-is or default
      // Reason: [issue.reason || 'Potential Risk'],
      Reason: [issue.reason_id ?? null, Validators.required],

      // Toggle risk
      IsRisk: [Boolean(issue.is_risk)],
      current_state: [issue.current_state]
    });
  }


  private rebuildRisksFormArray(issues: any[]): void {
    const fa = this.risksArray;   // FormArray
    fa.clear();
    issues.forEach(issue => fa.push(this.buildRiskFormGroup(issue)));
  }


  /** Convenient getter used already in template */
  get risksArray(): FormArray {
    return this.risksForm.get('risks') as FormArray;
  }


  // Stub methods for UI actions
  onEditRisks(): void {
    this.rebuildRisksFormArray(this.issuesFromApi);
    this.isRisksEdit = true;
  }

  onSaveRisks(): void {
    this.toasterService.clearAll();
    this.loaderService.show();
    // Validate form before preparing payload
    if (this.risksForm.invalid) {
      this.loaderService.hide();
      this.risksForm.markAllAsTouched();
      console.warn('[Notes] Cannot save: form is invalid.', this.risksForm.value);
      return;
    }

    const payload = this.buildSavePayload();

    // Exit edit mode (keep this behavior as you had)
    this.isRisksEdit = false;

    // Later: call API here

    this.apiService.saveRisksAPI(payload).subscribe({
      next: (res: any) => {
        this.getNotes();
        this.apiService.triggerRefresh();
        this.loaderService.hide();
        this.toasterService.setError({
          message: 'Issue(s) successfully updated.',
          type: 'success',
        });
      },
      error: (err: any) => {
        console.error('[Notes] Save failed →', err)
        this.toasterService.setError({
          message: 'Unable to save the data.',
          type: 'error',
        });
        this.loaderService.hide();
      },
    });

  }

  getNotes() {
    const payload = {
      AssociateId: this.selectedAssociateId,
      PFLMId: this.selectedPflmId,
      IsGenAIRO: this.isRiskOfficer
    };
    const userId = this.userDetails.eid || 0;
    this.loaderService.show();
    this.apiService.getNotesDataAPI(payload, userId).subscribe({
      next: (response: any) => {
        this.loaderService.hide();
        this.apiService.openNotes({
          open: true,
          data: response
        });
      },
      error: (e: any) => {
        this.loaderService.hide();
        console.error('Error fetching notes:', e);
        this.toasterService.setError({
          message: 'Unable to fetch notes data.',
          type: 'error',
        });
      }
    });
  }

  getSeverityClass(id: number): string {
    const severity = this.getSeverityText(id);
    switch (severity) {
      case 'High':
        return this.riskHighClass;
      case 'Medium':
        return this.riskMediumClass;
      case 'Low':
        return this.riskLowClass;
      default:
        return ''; // Or a default class if needed
    }
  }
  /**
   * Get Severity text
   * @param id
   * @returns
   */
  getSeverityText(id: number): string {
    let severity = '';
    this.severityData.filter((item: any) => {
      if (+item.severity_id === +id) return (severity = item.severity);
    });
    return severity;
  }

  getStatusText(statusId: number): string {
    if (!statusId || !Array.isArray(this.statusData)) return 'Unknown';
    const status =
      this.statusData.find((s: any) =>
        // new API shape
        s.issue_status_id === statusId ||
        // legacy local shape (if still present somewhere)
        s.IssueStatusId === statusId
      );
    return status
      ? (status.issue_description || status.IssueStatus || 'Unknown')
      : 'Unknown';
  }

  getImpactText(impactId: number): string {
    if (!impactId || !Array.isArray(this.impactAreaData)) return 'Unknown';
    const impact =
      this.impactAreaData.find((i: any) =>
        // new API shape
        i.impact_id === impactId ||
        // legacy local shape
        i.Impact_Id === impactId
      );
    return impact
      ? (impact.impact || impact.Impact || 'Unknown')
      : 'Unknown';
  }

  /** Builds the save payload from the edit form + local state */
  private buildSavePayload(): SavePayload {
    const risksFa = this.risksArray; // FormArray
    const overallSummary = this.riskSummary?.trim() ?? '';

    // Helper: map statusId -> status text (CurrentState expects string)
    const resolveStatusText = (statusId: number): string => {
      if (!statusId || !Array.isArray(this.statusData)) return 'Unknown';
      const s =
        this.statusData.find((it: any) =>
          it.issue_status_id === statusId || it.IssueStatusId === statusId
        );
      return s ? (s.issue_description || s.IssueStatus || 'Unknown') : 'Unknown';
    };

    const riskLst: RiskItemPayload[] = risksFa.controls.map((ctrl: any) => {
      const v = ctrl.value;

      // v.issue_id comes from raw API; if your API uses IssueId for existing ones,
      // keep mapping here as a number (0 for new)
      const issueId = Number(v.issue_id ?? 0);

      const item: RiskItemPayload = {
        IssueId: issueId,
        RiskStatement: String(v.RiskStatment ?? '').trim(),
        RiskDescription: String(v.RiskDescription ?? '').trim(),
        ImpactId: Number(v.Impact_Area ?? 0),
        RiskSeverity: Number(v.Risk_Severity ?? 0),
        RiskStatus: Number(v.Status ?? 0),
        FTE: Number(v.Total_FTE ?? 0),
        BusinessImpact: String(v.Business_Impact ?? '').trim(),
        RevenueImpact: Number(v.Revenue_Impact ?? 0),
        MarginImpact: Number(v.Margin_Impact ?? 0),
        CurrentState: String(v.current_state ?? '').trim(),
        OverallSummary: overallSummary,
        IsRisk: Boolean(v.IsRisk),
        Comments: String(v.Comments ?? '').trim(),
        ReasonId: Number(v.Reason)
      };

      return item;
    });

    return {
      RiskLst: riskLst,
      AssociateId: this.selectedAssociateId,
      PFLMId: this.selectedPflmId,
      IsNewRiskList: false,
    };
  }


  /**
   * Read-only list: checkbox change → sync Reason + IsRisk + readOnlyRisk[]
   * @param risk   The risk item bound in the *ngFor (read-only view model)
   * @param event  DOM event from checkbox
   * @param index  index in risks array / risksArray form
   */

  onRiskChange(risk: any, event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;
    const issueId = Number(input.value ?? risk?.IssueId ?? 0);

    const riskArray = this.risksForm.get('risks') as FormArray;
    const isRiskCtrl = riskArray.at(index)?.get('IsRisk') as FormControl;
    const reasonCtrl = riskArray.at(index)?.get('Reason') as FormControl;

    if (checked) {
      const potentialRiskId = this.reasonsData.find(r => r.reason === 'Potential Risk')?.id;
      risk.ReasonId = potentialRiskId;
      reasonCtrl?.setValue(potentialRiskId);
      risk.IsRisk = true;
      isRiskCtrl?.setValue(true);

      // 🔑 Add to readOnlyRisk array instantly so child component shifts it to Risks
      if (!this.readOnlyRisk.includes(issueId)) {
        this.readOnlyRisk = [...this.readOnlyRisk, issueId];
      }
    } else {
      const notAnIssueId = this.reasonsData.find(r => r.reason === 'Not an Issue')?.id;
      const issueReasonId = this.reasonsData.find(r => r.reason === 'Issue')?.id;
      risk.ReasonId = risk.ReasonId === notAnIssueId ? notAnIssueId : issueReasonId;
      reasonCtrl?.setValue(risk.ReasonId);
      risk.IsRisk = false;
      isRiskCtrl?.setValue(false);

      // 🔑 Remove from readOnlyRisk array instantly so child component shifts it to Issues
      this.readOnlyRisk = this.readOnlyRisk.filter(id => id !== issueId);
    }

    // 🔑 Keep issuesFromApi in sync too
    const apiRisk = this.issuesFromApi.find((r: any) => r.issue_id === issueId);
    if (apiRisk) {
      apiRisk.reason_id = risk.ReasonId;
      apiRisk.is_risk = risk.IsRisk;
    }

    this.checkCommentsError(index);
  }
  /**
   * Edit mode: reason dropdown change → sync IsRisk + readOnlyRisk[]
   * @param index  index in FormArray
   * @param event  DOM event from <select>
   */

  onReasonChangeEdit(index: number, event: Event): void {
    const newReasonId = Number((event.target as HTMLSelectElement).value);

    const riskArray = this.risksForm.get('risks') as FormArray;
    const riskGroup = riskArray.at(index) as FormGroup;

    const isRiskCtrl = riskGroup.get('IsRisk');
    const reasonCtrl = riskGroup.get('Reason');
    const issueId = Number(riskGroup.get('issue_id')?.value ?? 0);

    // Update form control with numeric ID
    reasonCtrl?.setValue(newReasonId);

    // Look up selected reason in master list
    const selectedReason = this.reasonsData.find(r => r.id === newReasonId);

    if (!selectedReason) return;

    // Determine if the selected reason corresponds to Potential Risk
    const isPotentialRisk = selectedReason.reason === 'Potential Risk';

    if (isPotentialRisk) {
      isRiskCtrl?.setValue(true);

      if (!this.readOnlyRisk.includes(issueId)) {
        this.readOnlyRisk.push(issueId);
      }

    } else {
      // For "Issue" and "Not an Issue"
      isRiskCtrl?.setValue(false);

      this.readOnlyRisk = this.readOnlyRisk.filter(id => id !== issueId);
    }
  }

  /**
   * Read-only list: reason dropdown change → sync IsRisk + checkbox visual + readOnlyRisk[]
   * @param risk   risk item in read-only list (view model)
   * @param index  row index (used for checkbox element id)
   * @param event  DOM event from <select>
   */

  onReasonChange(risk: any, index: number, event: Event): void {
    const newReasonId = Number((event.target as HTMLSelectElement).value);

    const riskArray = this.risksForm.get('risks') as FormArray;
    const reasonCtrl = riskArray.at(index)?.get('Reason') as FormControl;
    const isRiskCtrl = riskArray.at(index)?.get('IsRisk') as FormControl;

    // Update form control
    reasonCtrl?.setValue(newReasonId);

    // Checkbox reference in view mode
    const chkElement = document.getElementById(`riskCheckbox_${index}`) as HTMLInputElement | null;

    // Find reason object
    const selectedReason = this.reasonsData.find(r => r.id === newReasonId);

    if (!selectedReason) return; // safety check

    // Determine if reason implies risk
    const isPotentialRisk = selectedReason.reason === 'Potential Risk';

    if (isPotentialRisk) {
      // Check the checkbox in view mode
      if (chkElement && !chkElement.checked) chkElement.checked = true;

      risk.IsRisk = true;
      isRiskCtrl?.setValue(true);

      // Update the model correctly
      risk.ReasonId = newReasonId;

      if (!this.readOnlyRisk.includes(risk.IssueId)) {
        this.readOnlyRisk.push(risk.IssueId);
      }
    } else {
      // Uncheck checkbox
      if (chkElement && chkElement.checked) chkElement.checked = false;

      risk.IsRisk = false;
      isRiskCtrl?.setValue(false);

      risk.ReasonId = newReasonId;

      this.readOnlyRisk = this.readOnlyRisk.filter((id: number) => id !== risk.IssueId);
    }

    this.checkCommentsError(index);

    if (!this.isRisksEdit) {
      this.onSaveRisks(); // auto-save in read-only mode
    }
  }


  onSupervisorSubmit() {
    // new change save and submit button navigation start
    for (let i = 0; i < this.risks.length; i++) {
      if (this.isCommentDirty(i)) {
        const element = document.getElementById(`riskCommentsBox_${i}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    }

    // new change save and submit button navigation end
    // new change save and submit start
    if (this.risks.some((_: any, index: number) => this.isCommentDirty(index))) {
      // Block submission if any comment is dirty.
      // The UI will already show the necessary highlights and messages.
      return;
    }
    // new change save and submit end

    let RiskLstVal = this.risks.map((risk: any) => ({
      IssueId: risk.IssueId,
      // IsRisk: risk.Reason === 'Potential Risk' ? true : false,
      IsRisk: risk.Reason === 'Potential Risk' ? true : false,
      Comments: risk.Comments,
      Reason: risk.Reason,
    }));
    this.loaderService.show();
    // let riskPayload = this.appService.getRiskPayload();
    const supervisorSubmitPayload = {
      AssociateId: this.selectedAssociateId,
      PFLMId: this.selectedPflmId,
      RiskLst: RiskLstVal,
    };
    this.apiService.supervisorSubmitsAPI(supervisorSubmitPayload).subscribe({
      next: (response) => {
        this.loaderService.hide();
        //added this fetch method to update the UI with latest risk data
        this.toasterService.setError({
          message: 'Successfully submitted to risk officer for further review. No further action required.',
          type: 'success',
        });
        //refresh the data
        this.apiService.triggerRefresh();
        this.closeNotes();
      },
      error: (e: any) => {
        this.loaderService.hide();
        const error = e?.message || e?.statusText;
        console.log('Error in supervisor submit: ' + error);
        this.toasterService.setError({
          message: 'Unable to fetch the data.',
          type: 'error',
        });
        this.loaderService.hide();
      },
      complete: () => {
        this.loaderService.hide();
      },
    });
    //new api method for supervisor submit button end
  }

  //new change for save and submit button start
  isCommentDirty(index: number): boolean {
    if (!this.risks[index] || this.originalRiskComments[index] === undefined) {
      return false;
    }
    const currentComment = this.risks[index].Comments || '';
    const originalComment = this.originalRiskComments[index] || '';
    return currentComment !== originalComment;
  }
  //new change for save and submit button end

  getButtonTooltipRo(): string {
    if (this.isRisksValid() || this.isROFormInvalid()) {
      return 'Please enter all the required values.';
    }
    return this.readOnlyRisk?.length > 0 ? 'Click to Review and log into Risk Register.' : 'Click to Update status in DHD FLM';
  }

  isROFormInvalid(): boolean {
    if (!this.isRiskOfficer) return false;
    return this.risksForm.invalid;
  }
  getButtonTooltipSv(): string {
    return this.isRisksBlankVal() ? 'Please enter all the required values(Total FTE/Margin Impact/Revenue Impact).' : 'Submit all issues to the Risk Officer for review';
  }

  /**
 * Check for value is blank or not in read mode
 * @returns
 */
  isRisksBlankVal(): boolean {
    const result = this.risks.some((risk: any, index: number) => {
      const totalFte = risk?.FTE ?? '';
      const marginImpact = risk?.MarginImpact ?? '';
      const revenueImpact = risk?.RevenueImpact ?? '';
      return totalFte === '' || marginImpact === '' || revenueImpact === '';
    });
    return result;
  }

  /**
   * Allow user to submit the risks for risk officer
   * @returns
   */
  isRisksValid(): boolean {
    const result = this.risks.some((risk: any, index: number) => {
      const id = `riskCommentsBox_${index}`;
      const textarea = document.getElementById(id) as HTMLTextAreaElement;
      const checkbox = `riskCheckbox_${index}`;
      const isRisk = (document.getElementById(checkbox) as HTMLInputElement).checked;
      return !isRisk && textarea.value.trim() === '';
    });
    return result;
  }

  /**
* Get css class
* @returns
*/
  getRiskBlock(): string {
    let cssClass = 'risk-content-edit-block';
    if (this.isRisksEdit && !this.isSummaryEdit) cssClass = 'risk-content-edit-block';
    else if (this.isRisksEdit && this.isSummaryEdit) cssClass = 'risk-content-both-block';
    else if (this.isSummaryEdit) {
      cssClass = 'risk-content-summary-edit-block';
    }
    return cssClass;
  }


  formatNumbersInString(value: any): string {
    const formatIfNeeded = (match: string, dollar: string, rawNumber: string) => {
      const numeric = rawNumber.replace(/,/g, '');
      const parsed = Number(numeric);

      if (isNaN(parsed)) return match;

      // Detect original decimal precision
      const hasDecimal = rawNumber.includes('.');
      const decimalPlaces = hasDecimal ? (rawNumber.split('.')[1]?.length || 0) : 0;

      const format = `1.${decimalPlaces}-${decimalPlaces}`;
      const formatted = parsed >= 1000
        ? this.decimalPipe.transform(parsed, format)
        : parsed.toString();

      return (dollar ?? '') + (formatted ?? match);
    };

    if (typeof value === 'number' || this.apiService.isNumeric(value)) {
      const valueStr = value.toString();
      const hasDecimal = valueStr.includes('.');
      const decimalPlaces = hasDecimal ? (valueStr.split('.')[1]?.length || 0) : 0;
      const format = `1.${decimalPlaces}-${decimalPlaces}`;

      return Number(value) >= 1000
        ? this.decimalPipe.transform(Number(value), format) ?? valueStr
        : valueStr;
    }

    if (typeof value === 'string') {
      return value.replace(/\b(\$)?([\d,]+(?:\.\d+)?)\b/g, formatIfNeeded);
    }

    return String(value ?? '');
  }

  //Validation Helpers start


  allowOnlyDigits(event: KeyboardEvent): void {
    this.commonService.allowDigitsOnly(event);
  }

  allowDigitsWithDecimal(event: KeyboardEvent): void {
    this.commonService.allowDigitsWithDecimal(event);
  }

  checkError(i: number, controlName: string): boolean {
    const group = this.risksArray.at(i) as FormGroup;
    const ctrl = group.get(controlName) as FormControl;
    return !!ctrl && (ctrl.touched || ctrl.dirty) && ctrl.invalid;
  }

  /**
   *
   * @param index
   */
  checkMaxLength(index: number, divId: string, textareaId: string, maxCount = 0): void {
    const divElement = `${divId}${index}`;
    const div = document.getElementById(divElement) as HTMLElement;
    if (div) {
      textareaId = `${textareaId}${index}`;
      const textarea = document.getElementById(textareaId) as HTMLTextAreaElement;
      const val = maxCount - textarea.value.length;
      div.innerHTML = `Max. Char ${val}`;
    }

    //Update comments value in formcontrol
    let id = `riskCommentsBox_${index}`;
    if (textareaId === id) {
      const textarea = document.getElementById(id) as HTMLTextAreaElement;
      const riskArray = this.risksForm.get('risks') as FormArray;
      const riskComments = riskArray.controls[index].get('Comments') as FormControl;
      riskComments.setValue(textarea.value.trim() || '');
    }
  }


  checkCommentsError(i: number): boolean {
    const group = this.risksArray.at(i) as FormGroup;
    const isRisk = Boolean(group.get('IsRisk')?.value);
    const comments = String(group.get('Comments')?.value ?? '').trim();
    return !isRisk && comments.length === 0;
  }


  isAnyDynamicFormInValid(): boolean {
    const fa = this.risksArray;
    return fa.controls.some(ctrl => (ctrl as FormGroup).invalid);
  }

  /**
  * Make enable/disable the risk form
  * @param i
  * @param event
  */
  toggleRisk(i: number, event: Event): void {
    const formArr = this.risksForm.get('risks') as FormArray;
    const formgr = formArr.at(i) as FormGroup;
    formgr.enable();
  }
  preventDragDrop(event: DragEvent): void {
    event.preventDefault();
  }

  onCommentInput(index: number): void {
    const textarea = document.getElementById(`riskCommentsBox_${index}`) as HTMLTextAreaElement;
    if (!textarea) return;
    const value = textarea.value.trim();
    this.risks[index].Comments = value;
    const ctrl = this.risksArray.at(index).get('Comments');
    if (ctrl) {
      ctrl.setValue(value);
      ctrl.markAsTouched();
    }
    this.checkCommentsError(index);
  }
  //Validation Helpers end
  openSidebar() {
    this.isSidebarVisible = true;
  }

  private sanitizeHtml(html: string): string {
    const decoded = this.decodeHtml(html);
    const div = document.createElement('div');
    div.innerHTML = this.sanitizer.sanitize(
      SecurityContext.HTML,
      decoded
    ) || '';
    return div.textContent || '';
  }

  private decodeHtml(encodedHtml: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = encodedHtml;
    return textarea.value;
  }

  get isSupervisor(): boolean {
    const currentRole = (this.role || '').toLowerCase();
    return currentRole === 'supervisor' || currentRole === 'supervisors';
  }

  get isRiskOfficer(): boolean {
    const normalizedRole = (this.role || '').trim().toLowerCase();
    return normalizedRole === 'riskofficer' || normalizedRole === 'riskofficers';
  }
}